import {
  Box,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { Options } from 'highcharts';
import HighchartsChart from '@components/charts/HighchartsChart';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';

/**
 * A chart and its accessible data-table equivalent, in one panel.
 *
 * Extracted from HomePage for REV-711 so the scan-results page gets the same
 * thing rather than a second implementation that drifts. The parts worth not
 * losing, both learned the hard way:
 *
 *   - `accessibleName` is REQUIRED, not optional. Without it Highcharts names
 *     every chart's region landmark "Chart. Highcharts interactive chart.", so
 *     a page with several charts gets several identically-named regions and
 *     the landmark list stops being navigable (STRUCTURE-21, REV-504).
 *   - The data table is not decoration. A chart alone is a single information
 *     channel; the table is what makes the panel pass 1.4.1, and it is why the
 *     e2e gate asserts every chart has one.
 */
interface ChartCardProps {
  title: string;
  description: string;
  options: Options | null;
  /**
   * When false, the panel says so in a sentence instead of rendering a table
   * with headers and no rows. Optional so HomePage's callers, which always
   * have data by the time they build a card, are unaffected.
   */
  hasData?: boolean;
  emptyText?: string;
  tableLabel: string;
  columns: string[];
  /**
   * REV-481: each row carries its own href rather than deriving one from the
   * rendered cells. The first cell is a DISPLAY label — a tracked place's name,
   * a rule's name — and the filter needs the underlying context_id / rule_id,
   * which only the caller has.
   */
  rows: Array<{ cells: string[]; to?: string }>;
  /** Span the whole row, for a chart that stands alone rather than in a pair. */
  fullWidth?: boolean;
}

function ChartCard({
  title,
  description,
  options,
  tableLabel,
  columns,
  rows,
  hasData = true,
  emptyText,
  fullWidth = false,
}: ChartCardProps) {
  return (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <Paper sx={{ p: 3, height: '100%' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {title}
        </Typography>
        {/* accessibleName is not optional on this page: without it Highcharts
            names every chart's region landmark "Chart. Highcharts interactive
            chart.", so six charts produce six identically-named regions and
            the landmark list is useless for navigation (STRUCTURE-21). The
            mechanism arrived with REV-504; this page never passed it. */}
        {options && (
          <HighchartsChart options={options} description={description} accessibleName={title} />
        )}
        {/* An empty table with headers and no rows reads as a broken panel. A
            sentence reads as an answer. */}
        {!hasData && emptyText && (
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        )}
        {/* Accessible data-table equivalent for the chart (WCAG dogfood). */}
        {hasData && (
          <Box component="details" sx={{ mt: 2 }}>
            <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main' }}>
              {tableLabel}
            </Box>
            <ScrollableTableContainer sx={{ mt: 1 }}>
              <Table size="small" aria-label={title}>
                <TableHead>
                  <TableRow>
                    {columns.map((c, i) => (
                      <TableCell key={c} align={i === 0 ? 'left' : 'right'}>
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.to ?? r.cells[0]}>
                      {r.cells.map((cell, i) => (
                        <TableCell key={i} align={i === 0 ? 'left' : 'right'}>
                          {/* Only the label cell links. Linking the count too
                            would give the row two links to the same place,
                            doubling the tab stops for no added reach. */}
                          {i === 0 && r.to ? (
                            <Link component={RouterLink} to={r.to} underline="hover">
                              {cell}
                            </Link>
                          ) : (
                            cell
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
          </Box>
        )}
      </Paper>
    </Grid>
  );
}

export default ChartCard;
